import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Col, Row, Spinner } from 'react-bootstrap';

import AppLayout from '../../layouts/AppLayout/AppLayout';
import { NioSection } from '../index';
import ServiceDetailsSection from '../ServiceDetailsSection';
import { api } from '../../utils/api';
import { useTranslation } from '../../i18n/I18nProvider';
import { translateCategory, translateCategoryField, translateSubKey } from '../../i18n/translateService';
import { catalogFr } from '../../i18n/locales/fr';

function ServiceCategoryPage({ categoryId }) {
  const { t, language } = useTranslation();
  const params = useParams();
  const slug = categoryId || params.slug;
  const [apiService, setApiService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    const fetchService = async () => {
      setLoading(true);
      try {
        const res = await api.getService(slug);
        if (res?.success && res.data) {
          setApiService(res.data);
        } else {
          // Fallback: try fetching all services and match by slug or title
          const allRes = await api.getServices({});
          if (allRes?.success && Array.isArray(allRes.data)) {
            const found = allRes.data.find((s) => {
              if (s.slug && s.slug === slug) return true;
              const titleSlug = String(s.title || '').toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              return titleSlug === slug;
            });
            if (found) setApiService(found);
          }
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  const parentName = apiService?.titles?.[language] || apiService?.title || translateCategory(t, slug);
  const pageTitle = t('serviceCatalog.subServicesTitle', { name: parentName });
  const subtitle = apiService?.subtitles?.[language] || apiService?.subtitle || translateCategoryField(t, slug, 'subtitle');
  const tilePrefix = apiService?.tilePrefix || 'service';
  const routeId = apiService?._id || encodeURIComponent(apiService?.title || slug);
  const tileClass = `${tilePrefix}-tile`;

  const subItems = useMemo(
    () =>
      (apiService?.subServices || []).map((sub) => ({
        ...sub,
        label: sub.labels?.[language] || sub.label || translateSubKey(t, sub.key),
        canonical: catalogFr?.sub?.[sub.key] || sub.key,
      })),
    [apiService, t, language],
  );

  if (loading) {
    return (
      <AppLayout title={t('common.loading')} rootClass="layout-1">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AppLayout>
    );
  }

  if (!apiService) return null;

  return (
    <AppLayout title={pageTitle} rootClass="layout-1">
      <style>{`
        .${tileClass} {
          position: relative;
          display: block;
          overflow: hidden;
          border-radius: 16px;
          min-height: 210px;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
          transition: box-shadow 0.25s ease, transform 0.25s ease;
        }
        .${tileClass}:hover {
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.2);
          transform: translateY(-2px);
        }
        .${tileClass}__bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        .${tileClass}__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(10, 20, 35, 0.12) 0%, rgba(10, 20, 35, 0.34) 100%);
        }
        .${tileClass}__title {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          margin: 0;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 700;
          text-align: center;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.8);
          font-size: clamp(0.85rem, 1.4vw, 1rem);
        }
      `}</style>

      <NioSection className="overflow-hidden pt-120 pt-lg-160" masks={['blur-1 left center']}>
        <NioSection.Content>
          <Row className="justify-content-center text-center mb-4">
            <Col lg={8}>
              <h2 className="mb-2">{pageTitle}</h2>
              <p className="text-muted mb-0">{subtitle}</p>
            </Col>
          </Row>

          {subItems.length > 0 ? (
            <Row className="g-3 g-lg-4">
              {subItems.map((sub, idx) => {
                const isFeatured = apiService?.featuredKeys?.includes(sub.key);
                const colLg = isFeatured ? 6 : 4;
                const colXs = slug === 'demenagement' || slug === 'moving' ? 5 : 6;
                return (
                  <Col xs={colXs} md={colXs} lg={colLg} key={`${sub.key}-${idx}`}>
                    <Link
                      to={`/service/${routeId}/sub-service-details`}
                      state={{
                        selectedSubService: sub.canonical,
                        subServiceCover: sub.cover,
                        subServiceLabel: sub.label,
                        subServiceDescription: sub.description,
                        subServiceItems: sub.items,
                        parentServiceTitle: apiService?.titles?.[language] || apiService?.title || parentName,
                        parentCategoryId: slug,
                      }}
                      className={tileClass}
                    >
                      <div className={`${tileClass}__bg`} style={{ backgroundImage: `url(${sub.cover})` }} />
                      <div className={`${tileClass}__overlay`} aria-hidden />
                      <span className={`${tileClass}__title`}>{sub.label}</span>
                    </Link>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center text-muted py-5">
              <p>{t('serviceCatalog.noSubServices')}</p>
            </div>
          )}
        </NioSection.Content>
      </NioSection>
      <ServiceDetailsSection categoryId={slug} serviceTitle={apiService?.titles?.[language] || apiService?.title || parentName} />
    </AppLayout>
  );
}

export default ServiceCategoryPage;
